import React from "react";
import { useWindowDimensions } from "react-native";
import { View, TouchableOpacity, Text, Colors } from "react-native-ui-lib";
import { Image } from "react-native";

export type StyleItem = {
  key: string;
  label?: string;
  image?: string;
  base64?: string;
};

interface MultiSelectProps {
  multiSelect: true;
  selectedStyles?: string[];
  selectedStyle?: never;
  onSelect: (selected: string[]) => void;
}

interface SingleSelectProps {
  multiSelect?: false;
  selectedStyles?: never;
  selectedStyle?: string;
  onSelect: (selected: string) => void;
}

type StylesGridProps = (MultiSelectProps | SingleSelectProps) & {
  styles: StyleItem[];
  maxColumns?: number;
  minColumns?: number;
  minItemSize?: number;
  horizontalPadding?: number;
  itemSpacing?: number;
  itemVerticalMargin?: number;
};

function chunkArray<T>(arr: T[], size: number): T[][] {
  return Array.from({ length: Math.ceil(arr.length / size) }, (_, i) =>
    arr.slice(i * size, i * size + size)
  );
}

export const StylesGrid: React.FC<StylesGridProps> = (props) => {
  const {
    styles,
    maxColumns = 6,
    minColumns = 2,
    minItemSize = 80,
    horizontalPadding = 32,
    itemSpacing = 16,
    itemVerticalMargin = 8,
  } = props;
  const { width: windowWidth } = useWindowDimensions();
  const totalWidth = windowWidth - horizontalPadding;
  const columns = Math.min(
    maxColumns,
    Math.max(minColumns, Math.floor(totalWidth / minItemSize))
  );
  const rows = chunkArray(styles, columns);

  // Unified Selection Checker
  function isSelected(key: string) {
    if (props.multiSelect) {
      return (props.selectedStyles ?? []).includes(key);
    } else {
      return props.selectedStyle === key;
    }
  }

  // Unified Selection Handler
  const handleSelect = (key: string) => {
    if (props.multiSelect) {
      const curr = props.selectedStyles ?? [];
      if (curr.includes(key)) {
        props.onSelect(curr.filter((k) => k !== key));
      } else {
        props.onSelect([...curr, key]);
      }
    } else {
      props.onSelect(key);
    }
  };

  return (
    <View style={{ paddingHorizontal: horizontalPadding / 2 }}>
      {rows.map((row, rowIndex) => (
        <View key={rowIndex} row style={{ marginBottom: itemSpacing }}>
          {row.map((style, colIdx) => (
            <View
              key={style.key}
              style={{
                marginLeft: colIdx === 0 ? 0 : itemSpacing / 2,
                marginRight: colIdx === row.length - 1 ? 0 : itemSpacing / 2,
                flex: 1,
                aspectRatio: 1,
                position: "relative",
                marginVertical: itemVerticalMargin,
              }}
            >
              <TouchableOpacity
                style={{
                  backgroundColor: "#fff",
                  borderWidth: isSelected(style.key) ? 2 : 1,
                  borderColor: isSelected(style.key) ? Colors.primary : "#ddd",
                  borderRadius: 8,
                  alignItems: "center",
                  justifyContent: "center",
                  overflow: "hidden",
                  height: "100%",
                  width: "100%",
                }}
                onPress={() => handleSelect(style.key)}
                activeOpacity={0.8}
              >
                {style.base64 ? (
                  <Image
                    source={{ uri: `data:image/png;base64,${style.base64}` }}
                    style={{ width: "100%", height: "100%" }}
                    resizeMode="contain"
                  />
                ) : null}
              </TouchableOpacity>
              {style.label ? (
                <View
                  style={{
                    alignContent: "center",
                    marginTop: 4,
                  }}
                >
                  <Text style={{ textAlign: "center" }}>{style.label}</Text>
                </View>
              ) : null}
            </View>
          ))}
          {row.length < columns &&
            Array.from({ length: columns - row.length }).map((_, i) => (
              <View
                key={`empty-${i}`}
                flex
                style={{
                  aspectRatio: 1,
                  backgroundColor: "transparent",
                  marginLeft: row.length + i === 0 ? 0 : itemSpacing / 2,
                  marginRight:
                    row.length + i === columns - 1 ? 0 : itemSpacing / 2,
                  marginVertical: itemVerticalMargin, // VERTICAL MARGIN FOR EMPTY
                }}
              />
            ))}
        </View>
      ))}
    </View>
  );
};
